<template>
  <div class="warranty-claims px-0">
    <!-- Navegación de tabs -->
    <div class="warranty-tabs mb-4" v-if="isLoggedIn">
      <button 
        type="button"
        class="tab-button"
        :class="{ active: activeTab === 'form' }"
        @click="activeTab = 'form'"
      >
        <i class="fas fa-plus-circle me-2"></i>Nueva Solicitud
      </button>
      <button 
        type="button"
        class="tab-button"
        :class="{ active: activeTab === 'status' }"
        @click="activeTab = 'status'"
      >
        <i class="fas fa-list me-2"></i>Mis Solicitudes
      </button>
    </div>

    <!-- Tab: Estado de Solicitudes -->
    <div v-if="activeTab === 'status' && isLoggedIn" class="warranty-status-section">
      <div class="warranty-form px-lg-3 px-0">
        <h3 class="text-light mb-4">
          <i class="fas fa-clipboard-list me-2"></i>Estado de mis Solicitudes de Garantía
        </h3>
        
        <!-- Loading state -->
        <div v-if="isLoadingClaims" class="text-center py-5">
          <div class="spinner-border text-primary" role="status">
            <span class="visually-hidden">Cargando...</span>
          </div>
          <p class="text-muted mt-3">Cargando tus solicitudes...</p>
        </div>

        <!-- No claims state -->
        <div v-else-if="userClaims.length === 0" class="empty-state py-5 text-center">
          <i class="fas fa-clipboard-list fa-3x text-muted mb-3"></i>
          <h5 class="text-light">No tienes solicitudes de garantía</h5>
          <p class="text-muted">Cuando envíes una solicitud, aparecerá aquí con su estado actual.</p>
          <button 
            type="button" 
            class="btn btn-primary mt-3"
            @click="activeTab = 'form'"
          >
            <i class="fas fa-plus me-2"></i>Crear Primera Solicitud
          </button>
        </div>

        <!-- Claims list -->
        <div v-else class="warranty-claims-list">
          <div 
            v-for="claim in userClaims" 
            :key="claim.id"
            class="warranty-claim-card mb-4"
          >
            <div class="claim-header">
              <div class="claim-id">
                <i class="fas fa-hashtag me-1"></i>
                Solicitud #{{ claim.id }}
              </div>
              <div class="claim-status">
                <span class="status-badge" :class="getStatusClass(claim.status)">
                  <i :class="getStatusIcon(claim.status)" class="me-1"></i>
                  {{ getStatusText(claim.status) }}
                </span>
              </div>
            </div>

            <div class="claim-details">
              <div class="row">
                <div class="col-md-6">
                  <div class="info-row">
                    <i class="fas fa-seedling text-primary"></i>
                    <strong class="text-light me-2">Producto:</strong>
                    <span class="text-muted">{{ claim.product_name }}</span>
                  </div>
                  <div class="info-row" v-if="claim.order_number">
                    <i class="fas fa-shopping-cart text-primary"></i>
                    <strong class="text-light me-2">Pedido:</strong>
                    <span class="text-muted">#{{ claim.order_number }}</span>
                  </div>
                  <div class="info-row">
                    <i class="fas fa-calendar text-primary"></i>
                    <strong class="text-light me-2">Fecha de compra:</strong>
                    <span class="text-muted">{{ formatDate(claim.purchase_date) }}</span>
                  </div>
                  <div class="info-row">
                    <i class="fas fa-calendar-check text-primary"></i>
                    <strong class="text-light me-2">Fecha de germinación:</strong>
                    <span class="text-muted">{{ formatDate(claim.germination_date) }}</span>
                  </div>
                </div>
                <div class="col-md-6">
                  <div class="info-row">
                    <i class="fas fa-clock text-primary"></i>
                    <strong class="text-light me-2">Enviada:</strong>
                    <span class="text-muted">{{ formatDate(claim.created_at) }}</span>
                  </div>
                  <div class="info-row" v-if="claim.updated_at !== claim.created_at">
                    <i class="fas fa-sync text-primary"></i>
                    <strong class="text-light me-2">Actualizada:</strong>
                    <span class="text-muted">{{ formatDate(claim.updated_at) }}</span>
                  </div>
                  <div class="info-row" v-if="claim.admin_response">
                    <i class="fas fa-comment-alt text-primary"></i>
                    <strong class="text-light me-2">Respuesta:</strong>
                    <span class="text-muted">{{ claim.admin_response }}</span>
                  </div>
                </div>
              </div>

              <div class="claim-description mt-3">
                <strong class="text-light d-block mb-2">Descripción del problema:</strong>
                <p class="text-muted description-text">{{ claim.description }}</p>
              </div>

              <!-- Fotos si existen -->
              <div v-if="hasPhotos(claim)" class="claim-photos mt-3">
                <strong class="text-light d-block mb-2">Fotos adjuntas:</strong>
                <div class="photo-grid">
                  <div 
                    v-for="(photo, index) in getClaimPhotos(claim)" 
                    :key="index"
                    class="photo-item"
                    @click="openPhotoModal(photo)"
                  >
                    <img :src="photo" :alt="`Foto ${index + 1}`" />
                    <div class="photo-label">Foto {{ index + 1 }}</div>
                  </div>
                </div>
              </div>
            </div>

            <div class="claim-actions">
              <small class="text-muted">
                <i class="fas fa-info-circle me-1"></i>
                {{ getStatusDescription(claim.status) }}
              </small>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Tab: Formulario de Nueva Solicitud -->
    <form 
      v-if="activeTab === 'form'" 
      @submit.prevent="submitWarrantyClaim" 
      class="warranty-form"
    >
      <!-- Name - only shown if not logged in -->
      <div class="mb-4" v-if="!isLoggedIn">
        <label class="form-label text-light">Nombre</label>
        <input
          v-model="formData.name"
          class="form-control bg-dark text-light"
          required
          placeholder="Tu nombre completo"
        >
      </div>

      <!-- Email - only shown if not logged in -->
      <div class="mb-4" v-if="!isLoggedIn">
        <label class="form-label text-light">Email</label>
        <input
          v-model="formData.email"
          type="email"
          class="form-control bg-dark text-light"
          required
          placeholder="Tu correo electrónico"
        >
      </div>
      
      <!-- User information display if logged in -->
      <div class="mb-4 user-info-box" v-if="isLoggedIn && currentUser">
        <div class="d-flex align-items-center mb-3">
          <i class="fas fa-user-circle fa-2x text-primary me-2"></i>
          <div>
            <h5 class="mb-0 text-light">{{ formData.name }}</h5>
            <p class="mb-0 text-muted">{{ formData.email }}</p>
          </div>
        </div>
      </div>

      <!-- Order Selection -->
      <div class="mb-4">
        <label class="form-label text-light">Tipo de Compra</label>
        <div class="mb-3">
          <div class="form-check">
            <input 
              class="form-check-input" 
              type="radio" 
              name="purchaseType" 
              id="withOrder" 
              value="with-order"
              v-model="purchaseType"
              :disabled="!isLoggedIn || isLoadingOrders"
            >
            <label class="form-check-label text-light" for="withOrder">
              Producto comprado en la página web
            </label>
          </div>
          <div class="form-check">
            <input 
              class="form-check-input" 
              type="radio" 
              name="purchaseType" 
              id="withoutOrder" 
              value="without-order"
              v-model="purchaseType"
            >
            <label class="form-check-label text-light" for="withoutOrder">
              Producto comprado por fuera de la página
            </label>
          </div>
        </div>
      </div>

      <!-- Order Selection - only show if purchase type is with-order -->
      <div class="mb-4" v-if="purchaseType === 'with-order' && isLoggedIn">
        <label class="form-label text-light">Selecciona tu Pedido</label>
        <select 
          v-model="formData.order_id"
          class="form-select bg-dark text-light"
          required
          :disabled="isLoadingOrders"
        >
          <option value="" v-if="isLoadingOrders">Cargando pedidos...</option>
          <option value="" v-else-if="eligibleOrders.length === 0">No tienes pedidos elegibles</option>
          <option value="" v-else>Seleccionar pedido...</option>
          <option 
            v-for="order in eligibleOrders" 
            :key="order.id" 
            :value="order.id"
          >
            Pedido #{{ order.id }} - {{ formatDate(order.created_at) }} - {{ order.recipient_name || 'Cliente' }}
          </option>
        </select>
      </div>

      <!-- Product Selection - only show if order is selected -->
      <div class="mb-4" v-if="purchaseType === 'with-order' && formData.order_id">
        <label class="form-label text-light">Selecciona el Producto</label>
        <select 
          v-model="formData.product_id"
          class="form-select bg-dark text-light"
          required
        >
          <option value="">Seleccionar producto...</option>
          <option 
            v-for="product in getOrderProducts(formData.order_id)"
            :key="product.id"
            :value="product.id"
          >
            {{ product.name }}
          </option>
        </select>
      </div>

      <!-- Purchase Date - Auto-filled when order is selected -->
      <div class="mb-4">
        <label class="form-label text-light">Fecha de Compra</label>
        <input
          v-model="formData.purchase_date"
          type="date"
          class="form-control bg-dark text-light"
          required
          :readonly="purchaseType === 'with-order' && !!formData.order_id"
        >
        <small class="text-muted" v-if="purchaseType === 'with-order' && formData.order_id">
          Esta fecha se completó automáticamente desde tu pedido
        </small>
      </div>

      <!-- Germination Date -->
      <div class="mb-4">
        <label class="form-label text-light">Fecha de Germinación</label>
        <input
          v-model="formData.germination_date"
          type="date"
          class="form-control bg-dark text-light"
          required
        >
      </div>

      <!-- Product Name - Auto-filled when product is selected or manual entry -->
      <div class="mb-4">
        <label class="form-label text-light">Producto</label>
        <input 
          v-model="formData.product_name"
          class="form-control bg-dark text-light"
          required
          placeholder="Nombre del producto"
          :readonly="purchaseType === 'with-order' && !!formData.product_id"
        >
        <small class="text-muted" v-if="purchaseType === 'with-order' && formData.product_id">
          Este campo se completó automáticamente desde tu selección
        </small>
        <small class="text-muted" v-else-if="purchaseType === 'without-order'">
          Ingresa el nombre del producto tal como aparece en el envase
        </small>
      </div>

      <!-- Description -->
      <div class="mb-4">
        <label class="form-label text-light">Descripción del problema</label>
        <textarea
          v-model="formData.description"
          class="form-control bg-dark text-light"
          rows="4"
          required
          placeholder="Describe el problema que has tenido con la germinación..."
        ></textarea>
      </div>

      <!-- Photo Upload -->
      <div class="photo-upload-section mb-4">
        <label class="form-label text-light d-block">
          Fotos del proceso
          <small class="text-muted ms-2">(Máximo 3 fotos)</small>
        </label>
        <div class="photo-grid">
          <div 
            v-for="(_, index) in 3" 
            :key="index"
            class="photo-upload-box"
            :class="{
              'has-image': formData.photos[index],
              'is-dragover': dragoverIndex === index
            }"
            @click="triggerFileInput(index)"
            @dragover.prevent="dragoverIndex = index"
            @dragleave.prevent="dragoverIndex = null"
            @drop.prevent="handleFileDrop($event, index)"
          >
            <template v-if="formData.photos[index]">
              <img :src="photoPreview[index]" :alt="`Foto ${index + 1}`" class="preview-image" />
              <div class="photo-overlay">
                <button 
                  type="button" 
                  class="remove-photo-btn"
                  @click.stop="removePhoto(index)"
                >
                  <i class="fas fa-trash"></i>
                </button>
              </div>
            </template>
            <template v-else>
              <div class="upload-placeholder">
                <i class="fas fa-camera mb-2"></i>
                <span class="upload-text">
                  Arrastra o haz clic para subir<br>
                  <small class="text-muted">Foto {{ index + 1 }}</small>
                </span>
              </div>
            </template>
            <input 
              type="file"
              :ref="`fileInput${index}`"
              accept="image/*"
              class="d-none"
              @change="handleFileUpload($event, index)"
            />
          </div>
        </div>
      </div>

      <div class="d-grid">
        <button 
          type="submit" 
          class="btn btn-primary py-3"
          :disabled="isSubmitting || !isFormValid"
        >
          <span v-if="isSubmitting">
            <i class="fas fa-spinner fa-spin me-2"></i>Enviando...
          </span>
          <span v-else>
            <i class="fas fa-paper-plane me-2"></i>Enviar solicitud
          </span>
        </button>
      </div>
    </form>

    <!-- Photo Modal -->
    <div v-if="showPhotoModal" class="photo-modal" @click="closePhotoModal">
      <div class="photo-modal-content" @click.stop>
        <button class="close-button" @click="closePhotoModal">&times;</button>
        <img :src="currentPhoto" alt="Foto ampliada" />
      </div>
    </div>

    <!-- Loading Overlay -->
    <div v-if="isSubmitting" class="loading-overlay">
      <div class="spinner-container">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">Cargando...</span>
        </div>
        <p class="mt-3 text-light">Enviando solicitud...</p>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted, computed, watch } from 'vue';
import { useToast } from 'vue-toastification';
import { submitWarrantyClaim as submitWarrantyAPI, getWarrantyClaims } from '@/services/warranty';
import { getUserOrders } from '@/services/orders';
import { getUserInfo } from '@/services/auth';

const toast = useToast();

// Tab management
const activeTab = ref('form');
const isLoadingClaims = ref(false);
const userClaims = ref([]);

// Photo modal
const showPhotoModal = ref(false);
const currentPhoto = ref('');

const isSubmitting = ref(false);
const isLoadingOrders = ref(true);
const userOrders = ref([]);
const purchaseType = ref('without-order'); // Default to manual entry

// Computed property to check if the form is valid
const isFormValid = computed(() => {
  // Basic fields required for all users
  const basicFieldsValid = 
    formData.purchase_date &&
    formData.germination_date &&
    formData.product_name &&
    formData.description &&
    formData.photos.some(photo => photo);
  
  // Additional validation for online purchases
  if (purchaseType.value === 'with-order') {
    const orderFieldsValid = formData.order_id && formData.product_id;
    if (!orderFieldsValid) return false;
  }
  
  // Additional fields required only for non-logged in users
  if (!isLoggedIn.value) {
    return basicFieldsValid && formData.name && formData.email;
  }
  
  return basicFieldsValid;
});

// Check if the user is logged in
const isLoggedIn = ref(!!localStorage.getItem('accessToken'));
const currentUser = ref(null);

// Form data
const formData = reactive({
  name: '',
  email: '',
  order_id: '',
  product_id: '',
  purchase_date: '',
  germination_date: '',
  product_name: '',
  description: '',
  photos: [null, null, null]
});

// Photo previews
const photoPreview = reactive(['', '', '']);

// New state for drag and drop
const dragoverIndex = ref(null);

// Obtener órdenes elegibles para garantía (pending o completed)
const eligibleOrders = computed(() => {
  if (!userOrders.value || !Array.isArray(userOrders.value)) {
    console.log('No user orders or not an array:', userOrders.value)
    return []
  }
  
  const eligible = userOrders.value.filter(order => {
    const isValidStatus = order.status === 'completed' || order.status === 'pending'
    console.log(`Order #${order.id}: status=${order.status}, created=${order.created_at}, isValidStatus=${isValidStatus}`)
    return isValidStatus
  })
  
  console.log('Eligible orders for warranty:', eligible)
  return eligible
})

// Obtener productos de una orden específica
const getOrderProducts = (orderId) => {
  const order = userOrders.value?.find(o => o.id == orderId)
  console.log('Selected order:', order) // Para debug
  
  // La API devuelve order_products en lugar de products
  return order?.order_products?.map(p => ({
    id: p.product,
    name: p.product_name
  })) || []
}

// Función para formatear fechas
const formatDate = (date) => {
  return new Date(date).toLocaleDateString('es-ES')
}

// Watch para auto-completar datos cuando se selecciona una orden
watch(() => formData.order_id, (newOrderId) => {
  if (newOrderId && purchaseType.value === 'with-order') {
    const selectedOrder = userOrders.value.find(o => o.id == newOrderId)
    if (selectedOrder) {
      // Auto-completar fecha de compra
      formData.purchase_date = selectedOrder.created_at.split('T')[0]
      // Auto-completar nombre y email si no están ya completados
      if (!formData.name && selectedOrder.recipient_name) {
        formData.name = selectedOrder.recipient_name
      }
      if (!formData.email && selectedOrder.customer_email) {
        formData.email = selectedOrder.customer_email
      }
    }
    // Limpiar selección de producto cuando cambia la orden
    formData.product_id = ''
    formData.product_name = ''
  }
})

// Watch para limpiar campos cuando cambia el tipo de compra
watch(() => purchaseType.value, (newType) => {
  if (newType === 'without-order') {
    // Limpiar campos relacionados con pedidos
    formData.order_id = ''
    formData.product_id = ''
    formData.product_name = ''
    formData.purchase_date = ''
  } else if (newType === 'with-order') {
    // Limpiar campos manuales
    formData.product_name = ''
    formData.purchase_date = ''
  }
})

// Watch para auto-completar nombre del producto cuando se selecciona
watch(() => formData.product_id, (newProductId) => {
  if (newProductId && purchaseType.value === 'with-order') {
    const selectedProduct = getOrderProducts(formData.order_id).find(p => p.id == newProductId)
    if (selectedProduct) {
      formData.product_name = selectedProduct.name
    }
  }
})

// Ya no necesitamos cargar pedidos elegibles
// ya que solo permitimos ingreso manual

onMounted(async () => {
  // Limpiar caché de órdenes para forzar carga fresca
  localStorage.removeItem('userOrders');
  localStorage.removeItem('ordersTimestamp');
  
  // Set default tab: if logged in, show status first, otherwise show form
  if (isLoggedIn.value) {
    activeTab.value = 'status';
  } else {
    activeTab.value = 'form';
  }
  
  // Cargar todas las órdenes del usuario si está logueado
  if (isLoggedIn.value) {
    try {
      isLoadingOrders.value = true
      const orders = await getUserOrders()
      console.log('Orders loaded:', orders) // Para debug
      userOrders.value = orders || []
      
      // Si tiene pedidos, mostrar opción de pedido web por defecto
      if (orders && orders.length > 0) {
        purchaseType.value = 'with-order'
      }
    } catch (error) {
      console.error('Error loading user orders:', error)
      toast.error('Error al cargar tus pedidos')
    } finally {
      isLoadingOrders.value = false
    }
  } else {
    isLoadingOrders.value = false
  }
  
  // Load user info if logged in
  if (isLoggedIn.value) {
    try {
      const response = await getUserInfo();
      currentUser.value = response;
      // Pre-fill name and email from logged-in user
      formData.name = currentUser.value.first_name 
        ? `${currentUser.value.first_name} ${currentUser.value.last_name || ''}`
        : currentUser.value.username;
      formData.email = currentUser.value.email;
      
      // Load warranty claims for status tab
      await loadWarrantyClaims();
    } catch (error) {
      console.error('Error fetching user data:', error);
    }
  }
});

// Modified handleFileUpload to only handle preview
const handleFileUpload = async (event, index) => {
  const file = event.target.files[0];
  if (!file) return;

  if (!file.type.startsWith('image/')) {
    toast.error('Por favor selecciona una imagen válida');
    return;
  }

  if (file.size > 5 * 1024 * 1024) {
    toast.error('La imagen no debe superar los 5MB');
    return;
  }

  // Store file reference and create preview
  formData.photos[index] = file;
  const reader = new FileReader();
  reader.onload = (e) => {
    photoPreview[index] = e.target.result;
  };
  reader.readAsDataURL(file);
};

// Add drag and drop handling
const handleFileDrop = (event, index) => {
  dragoverIndex.value = null;
  const file = event.dataTransfer.files[0];
  if (file) {
    const fakeEvent = { target: { files: [file] } };
    handleFileUpload(fakeEvent, index);
  }
};

// Remove photo
const removePhoto = (index) => {
  formData.photos[index] = null;
  photoPreview[index] = '';
};

// Función para resetear el formulario
const resetForm = () => {
  formData.name = '';
  formData.email = '';
  formData.order_id = '';
  formData.product_id = '';
  formData.purchase_date = '';
  formData.germination_date = '';
  formData.product_name = '';
  formData.description = '';
  formData.photos = [null, null, null];
  photoPreview.splice(0, photoPreview.length, '', '', '');
  purchaseType.value = 'without-order';
};

// Modified submitWarrantyClaim
const submitWarrantyClaim = async () => {
  // Validar diferentes campos dependiendo del tipo de compra
  if (purchaseType.value === 'with-order') {
    // Validación para compras online
    if (!formData.order_id) {
      toast.error('Por favor selecciona una orden');
      return;
    }
    
    if (!formData.product_id) {
      toast.error('Por favor selecciona un producto');
      return;
    }
  }
  
  // Validar campos básicos para ambos tipos de compra
  if (isLoggedIn.value) {
    if (!formData.purchase_date || 
        !formData.germination_date || 
        !formData.product_name || 
        !formData.description ||
        !formData.photos.some(photo => photo)) {
      toast.error('Por favor completa todos los campos requeridos');
      return;
    }
  } else {
    if (!formData.name || !formData.email || 
        !formData.purchase_date || !formData.germination_date ||
        !formData.product_name || !formData.description ||
        !formData.photos.some(photo => photo)) {
      toast.error('Por favor completa todos los campos requeridos');
      return;
    }
  }

  try {
    isSubmitting.value = true;

    const warrantyData = {
      name: formData.name,
      email: formData.email,
      order_id: purchaseType.value === 'with-order' ? formData.order_id : null,
      product_id: purchaseType.value === 'with-order' ? formData.product_id : null,
      purchase_date: formData.purchase_date,
      germination_date: formData.germination_date,
      product_name: formData.product_name,
      description: formData.description,
      photos: formData.photos.filter(Boolean),
      use_authenticated_user: isLoggedIn.value,
      purchase_type: purchaseType.value
    };

    const response = await submitWarrantyAPI(warrantyData);
    console.log('Warranty claim submitted successfully:', response);
    
    // Mostrar toast de éxito
    toast.success('¡Solicitud enviada con éxito! Revisaremos tu caso y te contactaremos en un plazo de 48-72 horas hábiles.');
    
    // Resetear el formulario
    resetForm();
    
    // Si está logueado, cambiar al tab de estado y recargar solicitudes
    if (isLoggedIn.value) {
      activeTab.value = 'status';
      await loadWarrantyClaims();
    }

  } catch (error) {
    console.error('Error submitting warranty claim:', error);
    toast.error(error.message || 'Error al enviar el formulario');
    // Scroll hacia arriba para mostrar el mensaje de error
    window.scrollTo({ top: 0, behavior: 'smooth' });
  } finally {
    isSubmitting.value = false;
  }
};

// Updated triggerFileInput function
const triggerFileInput = (index) => {
  const fileInputs = document.querySelectorAll('input[type="file"]');
  if (fileInputs[index]) {
    fileInputs[index].click();
  }
};

// Functions for warranty claims status
const loadWarrantyClaims = async () => {
  if (!isLoggedIn.value) return;
  
  try {
    isLoadingClaims.value = true;
    const claims = await getWarrantyClaims();
    userClaims.value = claims || [];
    console.log('Loaded warranty claims:', claims);
  } catch (error) {
    console.error('Error loading warranty claims:', error);
    toast.error('Error al cargar las solicitudes de garantía');
  } finally {
    isLoadingClaims.value = false;
  }
};

const getStatusClass = (status) => {
  const statusClasses = {
    'pending': 'status-pending',
    'reviewing': 'status-reviewing', 
    'approved': 'status-approved',
    'rejected': 'status-rejected',
    'resolved': 'status-resolved'
  };
  return statusClasses[status] || 'status-pending';
};

const getStatusIcon = (status) => {
  const statusIcons = {
    'pending': 'fas fa-clock',
    'reviewing': 'fas fa-search',
    'approved': 'fas fa-check-circle',
    'rejected': 'fas fa-times-circle',
    'resolved': 'fas fa-check-double'
  };
  return statusIcons[status] || 'fas fa-clock';
};

const getStatusText = (status) => {
  const statusTexts = {
    'pending': 'Pendiente',
    'reviewing': 'En Revisión',
    'approved': 'Aprobada',
    'rejected': 'Rechazada',
    'resolved': 'Resuelta'
  };
  return statusTexts[status] || 'Pendiente';
};

const getStatusDescription = (status) => {
  const descriptions = {
    'pending': 'Tu solicitud ha sido recibida y está pendiente de revisión.',
    'reviewing': 'Estamos revisando tu solicitud y las fotos enviadas.',
    'approved': 'Tu solicitud ha sido aprobada. Nos pondremos en contacto contigo.',
    'rejected': 'Tu solicitud no cumple con los requisitos de garantía.',
    'resolved': 'Tu solicitud ha sido resuelta satisfactoriamente.'
  };
  return descriptions[status] || 'Estado no disponible';
};

const hasPhotos = (claim) => {
  return claim.photo1 || claim.photo2 || claim.photo3;
};

const getClaimPhotos = (claim) => {
  const photos = [];
  if (claim.photo1) photos.push(claim.photo1);
  if (claim.photo2) photos.push(claim.photo2);
  if (claim.photo3) photos.push(claim.photo3);
  return photos;
};

const openPhotoModal = (photoUrl) => {
  currentPhoto.value = photoUrl;
  showPhotoModal.value = true;
};

const closePhotoModal = () => {
  showPhotoModal.value = false;
  currentPhoto.value = '';
};

// Watch for tab changes to load data
watch(() => activeTab.value, (newTab) => {
  if (newTab === 'status' && isLoggedIn.value) {
    loadWarrantyClaims();
  }
});
</script>

<style scoped>
.warranty-claims {
  max-width: 800px;
  margin: 0 auto;
  padding: 2rem 1rem;
}

.warranty-form {
  background: var(--black-color-2);
  padding: 2rem;
  border-radius: 1rem;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.warranty-description {
  max-width: 600px;
  margin: 0 auto;
}

.progress-container {
  padding: 0 1rem;
}

.progress {
  height: 4px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 2px;
  margin-bottom: 1rem;
}

.progress-bar {
  height: 100%;
  background: var(--primary-color);
  border-radius: 2px;
  transition: width 0.3s ease;
}

.progress-labels {
  display: flex;
  justify-content: space-between;
  color: var(--text-muted);
  font-size: 0.9rem;
}

.progress-labels span {
  position: relative;
  padding-top: 0.5rem;
}

.progress-labels span::before {
  content: '';
  position: absolute;
  top: -0.75rem;
  left: 50%;
  transform: translateX(-50%);
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.1);
  transition: background-color 0.3s ease;
}

.progress-labels span.active {
  color: var(--primary-color);
}

.progress-labels span.active::before {
  background: var(--primary-color);
}

.loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
}

.spinner-container {
  text-align: center;
  background-color: rgba(0, 0, 0, 0.8);
  padding: 2rem;
  border-radius: 1rem;
}

.steps-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 2rem 0;
  position: relative;
  padding: 0 2rem;
}

.step {
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  z-index: 1;
  flex: 1;
}

.step::before {
  content: '';
  position: absolute;
  top: 1.5rem;
  left: -50%;
  width: 100%;
  height: 2px;
  background-color: var(--primary-color);
  opacity: 0.3;
}

.step:first-child::before {
  display: none;
}

.step.active .step-number {
  background-color: var(--primary-color);
  border-color: var(--primary-color);
}

.step.completed .step-number {
  background-color: var(--success-color);
  border-color: var(--success-color);
}

.step-number {
  width: 3rem;
  height: 3rem;
  border-radius: 50%;
  border: 2px solid rgba(255, 255, 255, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.1);
  color: white;
  margin-bottom: 0.5rem;
  transition: all 0.3s ease;
}

.step-text {
  color: var(--text-muted);
  font-size: 0.9rem;
}

.photo-upload-container {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 1rem;
  margin-top: 0.5rem;
}

.photo-upload-item {
  aspect-ratio: 1;
  border: 2px dashed var(--primary-color);
  border-radius: 12px;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.05);
  transition: all 0.3s ease;
}

.upload-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  color: var(--primary-color);
}

.upload-placeholder i {
  font-size: 1.5rem;
  margin-bottom: 0.5rem;
}

.upload-text {
  font-size: 0.9rem;
}

.photo-preview {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.remove-photo {
  position: absolute;
  top: 5px;
  right: 5px;
  background: rgba(0, 0, 0, 0.5);
  border: none;
  color: white;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.remove-photo:hover {
  background: rgba(0, 0, 0, 0.7);
}

.photo-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-top: 1rem;
}

.photo-upload-box {
  aspect-ratio: 3/2;
  border: 2px dashed var(--primary-color);
  border-radius: 12px;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.05);
  transition: all 0.2s ease;
}
.form-control {
  border: 1px solid var(--black-color-2);
}

.photo-upload-box:hover {
  border-color: var(--primary-color);
  background: rgba(255, 255, 255, 0.08);
}

.photo-upload-box.is-dragover {
  border-color: var(--primary-color);
  background: rgba(var(--primary-color-rgb), 0.1);
}

.upload-placeholder {
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: var(--primary-color);
  padding: 1rem;
  text-align: center;
}

.preview-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.photo-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.2s ease;
}

.photo-upload-box:hover .photo-overlay {
  opacity: 1;
}

.remove-photo-btn {
  background: var(--danger-color);
  border: none;
  color: white;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: transform 0.2s ease;
}

.remove-photo-btn:hover {
  transform: scale(1.1);
}

.user-info-box {
  background: rgba(255, 255, 255, 0.05);
  padding: 1rem;
  border-radius: 0.5rem;
}

.form-check {
  margin-bottom: 0.5rem;
}

.form-check-input {
  background-color: var(--black-color-2);
  border-color: var(--primary-color);
}

.form-check-input:checked {
  background-color: var(--primary-color);
  border-color: var(--primary-color);
}

.form-check-input:focus {
  box-shadow: 0 0 0 0.25rem rgba(var(--primary-color-rgb), 0.25);
}

.form-check-label {
  cursor: pointer;
}

/* Tab Styles */
.warranty-tabs {
  display: flex;
  gap: 0.5rem;
  background: var(--black-color-3);
  padding: 0.5rem;
  border-radius: 0.75rem;
  margin-bottom: 1.5rem;
}

.tab-button {
  flex: 1;
  padding: 0.75rem 1rem;
  background: transparent;
  border: none;
  color: var(--white-color-2);
  border-radius: 0.5rem;
  transition: all 0.3s ease;
  font-weight: 500;
  cursor: pointer;
}

.tab-button:hover {
  background: rgba(255, 255, 255, 0.05);
  color: var(--white-color);
}

.tab-button.active {
  background: var(--primary-color);
  color: white;
}

/* Warranty Status Styles */
.warranty-status-section {
  background: var(--black-color-2);
  border-radius: 1rem;
  padding: 2rem;
}

.warranty-claims-list {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.warranty-claim-card {
  background: var(--black-color-1);
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.1);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.warranty-claim-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.2);
}

.claim-header {
  padding: 1rem;
  background: rgba(0, 0, 0, 0.3);
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.claim-id {
  font-family: monospace;
  color: var(--white-color-2);
  font-size: 0.9rem;
  font-weight: 600;
}

.claim-status {
  display: flex;
  align-items: center;
}

.status-badge {
  padding: 0.375rem 0.75rem;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.status-pending {
  background: rgba(255, 193, 7, 0.2);
  color: #ffc107;
}

.status-reviewing {
  background: rgba(0, 123, 255, 0.2);
  color: #0d6efd;
}

.status-approved {
  background: rgba(40, 167, 69, 0.2);
  color: #28a745;
}

.status-rejected {
  background: rgba(220, 53, 69, 0.2);
  color: #dc3545;
}

.status-resolved {
  background: rgba(108, 117, 125, 0.2);
  color: #6c757d;
}

.claim-details {
  padding: 1.5rem;
}

.info-row {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  margin-bottom: 0.75rem;
}

.info-row i {
  width: 16px;
  text-align: center;
  margin-top: 0.125rem;
  flex-shrink: 0;
}

.claim-description {
  background: rgba(255, 255, 255, 0.05);
  padding: 1rem;
  border-radius: 0.5rem;
  border-left: 3px solid var(--primary-color);
}

.description-text {
  font-size: 0.9rem;
  color: var(--white-color-2);
  max-height: 100px;
  overflow-y: auto;
  line-height: 1.5;
  margin: 0;
}

.claim-photos {
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  padding-top: 1rem;
}

.photo-grid {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.photo-item {
  width: 80px;
  height: 80px;
  overflow: hidden;
  border-radius: 8px;
  cursor: pointer;
  position: relative;
  border: 2px solid rgba(255, 255, 255, 0.1);
  transition: transform 0.3s ease, border-color 0.3s ease;
}

.photo-item:hover {
  transform: scale(1.05);
  border-color: var(--primary-color);
}

.photo-item img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.photo-label {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: rgba(0, 0, 0, 0.8);
  color: white;
  font-size: 0.7rem;
  padding: 2px;
  text-align: center;
}

.claim-actions {
  padding: 1rem 1.5rem;
  background: rgba(0, 0, 0, 0.2);
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 3rem 2rem;
  color: var(--white-color-2);
  text-align: center;
}

.empty-state i {
  opacity: 0.5;
}

/* Photo Modal */
.photo-modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1100;
  backdrop-filter: blur(4px);
}

.photo-modal-content {
  max-width: 90vw;
  max-height: 90vh;
  position: relative;
}

.photo-modal-content img {
  max-width: 100%;
  max-height: 80vh;
  display: block;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.5);
}

.close-button {
  position: absolute;
  top: -50px;
  right: 0;
  background: none;
  border: none;
  color: white;
  font-size: 2rem;
  cursor: pointer;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.5);
  transition: background 0.3s ease;
}

.close-button:hover {
  background: rgba(0, 0, 0, 0.8);
}

@media (max-width: 768px) {
  .warranty-tabs {
    flex-direction: column;
  }
  
  .claim-header {
    flex-direction: column;
    gap: 0.75rem;
    align-items: flex-start;
  }
  
  .info-row {
    flex-direction: column;
    gap: 0.25rem;
  }
  
  .photo-grid {
    justify-content: center;
  }
}
</style>
